import React from 'react';
import { isFulfilled } from '@reduxjs/toolkit';

import { Button, Heading, Modal, Text } from '@auspost/postmaster-react';

import { useAppDispatch, useAppSelector } from 'store/store';

import { NotificationStatus, NotificationType } from '../../../../models/Notification';
import { createNotification } from '../../../../store/notification';
import { toggleEditMode } from '../../../../store/selectRemove/selectRemove.slice';
import { deleteShipments } from '../../../../store/shipment/details/shipmentDetails.slice';
import { successContent } from '../../../common/Notification/Notification.config';
import {
  SELECT_AND_REMOVE_MODAL_CONTENT_TEST_ID,
  SELECT_REMOVE_CANCEL_BUTTON_TEST_ID,
  SELECT_REMOVE_SUBMIT_BUTTON_TEST_ID,
} from '../SelectRemove.test-ids';

import styles from './SelectAndRemoveModal.module.scss';

interface SelectAndRemoveModalProp {
  isOpen: boolean;
  handleClose: () => void;
  checkedItems: string | null;
}

const SelectAndRemoveModal: React.FC<SelectAndRemoveModalProp> = ({ isOpen, handleClose, checkedItems }) => {
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector((state) => state.shipmentDetails.deleteShipment)?.loading;
  const selectedShipments = useAppSelector((state) => state.selectRemove).selectedShipments;
  const checked = selectedShipments?.filter((item) => item.checked);
  const selectedTrackingIds = checked?.map((shipment) => shipment.trackingId) ?? [];
  const selectedConsignmentIds = checked?.map((shipment) => shipment.consignmentId) ?? [];
  const selectedNickname = checked.length ? checked[0].nickname : null;

  const handleRemove = async () => {
    const deleteAction = await dispatch(
      deleteShipments({
        trackingIds: selectedTrackingIds,
        consignmentIds: selectedConsignmentIds,
        nickname: selectedNickname,
      })
    );

    if (checked.length < 1) return null;
    const { isMultiArticleConsignment } = checked[0];
    const trackingNumber = isMultiArticleConsignment ? selectedConsignmentIds : selectedTrackingIds;
    const successRemovePayload = {
      type: NotificationType.ALERT,
      status: NotificationStatus.SUCCESS,
      message: successContent.removeArticleFromTracklist(trackingNumber, selectedNickname),
    };
    handleClose();
    if (isFulfilled(deleteAction)) {
      dispatch(toggleEditMode({ editMode: false }));
      dispatch(createNotification(successRemovePayload));
    }
  };

  return (
    <Modal
      isModalShowing={isOpen}
      onHide={handleClose}
      modalClassName={styles.selectRemoveModal}
      dialogClassName={styles.selectRemoveDialog}
      ariaDialogLabel='select and remove item modal'
      dialogId='select and remove item modal'
    >
      <Modal.Header closeButton={false} className={styles.header}>
        <Heading level='h1'>Remove parcel?</Heading>
      </Modal.Header>

      <Modal.Content className={styles.content}>
        <Text variant='body' data-testid={SELECT_AND_REMOVE_MODAL_CONTENT_TEST_ID}>
          Are you sure you want to remove <span>{checkedItems}</span> from your list?
        </Text>
        <Text variant='body'>We&apos;ll also stop sending you any notifications for it.</Text>
      </Modal.Content>

      <Modal.Footer className={styles.footer}>
        <Button
          size='medium'
          variant='tertiary-filled'
          onClick={handleClose}
          data-testid={SELECT_REMOVE_CANCEL_BUTTON_TEST_ID}
        >
          Cancel
        </Button>
        <Button
          size='medium'
          variant='primary'
          isLoading={isLoading}
          onClick={handleRemove}
          data-testid={SELECT_REMOVE_SUBMIT_BUTTON_TEST_ID}
        >
          Remove
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SelectAndRemoveModal;
